import React, { useState } from 'react';

const Header = () => {
  // 모바일 메뉴 상태 관리
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // 메뉴 항목 클릭 시 모바일 메뉴 닫기
  const handleMenuClick = () => {
    setIsMobileMenuOpen(false);
  };

  // 스무스 스크롤 함수 (애니메이션 상태 고려)
  const scrollToSection = (sectionId) => {
    console.log('🔍 Scroll to:', sectionId);
    
    const element = document.querySelector(sectionId);
    if (element) {
      // 요소가 뷰포트에 있는지 확인
      const rect = element.getBoundingClientRect();
      const isInViewport = rect.top >= 0 && rect.top <= window.innerHeight;
      
      // 페이지 로드 후 시간 확인 (초기 애니메이션 고려)
      const pageLoadTime = performance.now();
      const isInitialLoad = pageLoadTime < 3000; // 3초 이내는 초기 로드로 간주
      
      // 해당 섹션의 애니메이션 상태 확인
      const sectionContainer = element.closest('section') || element.closest('[class*="transition"]');
      const hasOpacityAnimation = sectionContainer && 
        (sectionContainer.classList.contains('opacity-0') || 
         getComputedStyle(sectionContainer).opacity < 1);
      
      // 디버깅 정보 출력
      console.log('📊 Debug Info:', {
        sectionId,
        elementPosition: rect.top,
        isInViewport,
        pageLoadTime,
        isInitialLoad,
        hasOpacityAnimation,
        windowWidth: window.innerWidth,
        isMobile: window.innerWidth < 768,
        sectionContainer: sectionContainer?.tagName,
        opacity: sectionContainer ? getComputedStyle(sectionContainer).opacity : 'N/A',
        classList: sectionContainer ? Array.from(sectionContainer.classList) : []
      });
      
      // 항상 보정 적용 (모든 환경에서 일관된 스크롤 보장)
      const alwaysUseCorrection = true; // 항상 보정된 스크롤 사용
      if (alwaysUseCorrection) {
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset; // 절대 위치 계산
        
        // 애니메이션 오프셋은 렌더링되지 않았거나 애니메이션 진행 중일 때만 적용
        const needsAnimationOffset = !isInViewport || isInitialLoad || hasOpacityAnimation;
        const animationOffset = needsAnimationOffset ? 32 : 0; // translate-y-8 = 32px
        const baseOffset = window.innerWidth < 768 ? 500 : 150; // 데스크톱 80 → 150으로 증가
        let offsetPosition = absoluteElementTop - (baseOffset + animationOffset);
        
        // 이미 올바른 위치에 있으면 추가 스크롤하지 않음
        const currentScrollY = window.pageYOffset;
        const targetScrollY = Math.max(0, offsetPosition);
        const scrollDifference = Math.abs(currentScrollY - targetScrollY);
        
        // 50px 이내 차이면 이미 올바른 위치로 간주
        if (scrollDifference <= 50) {
          offsetPosition = currentScrollY; // 현재 위치 유지
        }
        
        console.log('✅ Using CORRECTED scroll:', {
          elementRelativePosition: elementRect.top,
          absoluteElementTop,
          needsAnimationOffset,
          animationOffset,
          baseOffset,
          targetScrollY,
          currentScrollY,
          scrollDifference,
          finalOffset: offsetPosition,
          isAlreadyInPosition: scrollDifference <= 50,
          reason: !isInViewport ? 'Not in viewport' : isInitialLoad ? 'Initial load' : hasOpacityAnimation ? 'Has animation' : 'Mobile'
        });
        
        window.scrollTo({
          top: Math.max(0, offsetPosition), // 음수가 되지 않도록 보정
          behavior: 'smooth'
        });
      } else {
        console.log('⚡ Using DEFAULT scroll (no correction needed)');
        // 애니메이션 완료 후에는 기본 스크롤
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    handleMenuClick();
  };

  return (
    // 헤더 컨테이너
    // - sticky: 스크롤 시 상단에 고정
    // - top-0: 화면 맨 위에 붙임
    // - bg-white/80: 80% 투명도의 흰색 배경
    // - backdrop-blur-md: 배경 블러 효과
    // - z-50: 다른 요소들 위에 표시되도록 높은 z-index
    // - shadow-sm: 작은 그림자 효과
    <header className="sticky top-0 bg-white/80 backdrop-blur-md shadow-sm z-50">
      <nav className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          {/* 로고/브랜드명 */}
          <button 
            onClick={() => scrollToSection('#top')}
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <img
              src="/images/logo.png"
              alt="The Moa Ventures"
              className="h-56 md:h-60 w-auto object-contain -my-28 md:-my-30 -ml-4"
            />
          </button>
          
          {/* 데스크톱 내비게이션 메뉴 */}
          <div className="hidden md:flex space-x-8 items-center">
            <button 
              onClick={() => scrollToSection('#calculator')}
              className="text-gray-600 hover:text-blue-500 transition-colors"
            >
              절세효과 계산기
            </button>
            <button 
              onClick={() => scrollToSection('#benefits')}
              className="text-gray-600 hover:text-blue-500 transition-colors"
            >
              핵심 혜택
            </button>
            <button 
              onClick={() => scrollToSection('#comparison')}
              className="text-gray-600 hover:text-blue-500 transition-colors"
            >
              상품 비교
            </button>
            <button 
              onClick={() => scrollToSection('#about')}
              className="text-gray-600 hover:text-blue-500 transition-colors"
            >
              회사 소개
            </button>
            <a
              href="https://open.kakao.com/o/skdqupSh"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors min-h-[44px] min-w-[80px] inline-flex items-center justify-center"
            >
              상담 문의
            </a>
          </div>

          {/* 모바일 햄버거 메뉴 버튼 */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="메뉴 토글"
          >
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M6 18L18 6M6 6l12 12" 
                />
              ) : (
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M4 6h16M4 12h16m-7 6h7" 
                />
              )}
            </svg>
          </button>
        </div>

        {/* 모바일 메뉴 */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-2 border-t border-gray-200 pt-4">
            <button
              onClick={() => scrollToSection('#calculator')}
              className="block w-full text-left py-2 text-gray-600 hover:text-blue-500 transition-colors min-h-[44px] flex items-center"
            >
              절세효과 계산기
            </button>
            <button
              onClick={() => scrollToSection('#benefits')}
              className="block w-full text-left py-2 text-gray-600 hover:text-blue-500 transition-colors min-h-[44px] flex items-center"
            >
              핵심 혜택
            </button>
            <button
              onClick={() => scrollToSection('#comparison')}
              className="block w-full text-left py-2 text-gray-600 hover:text-blue-500 transition-colors min-h-[44px] flex items-center"
            >
              상품 비교
            </button>
            <button
              onClick={() => scrollToSection('#about')}
              className="block w-full text-left py-2 text-gray-600 hover:text-blue-500 transition-colors min-h-[44px] flex items-center"
            >
              회사 소개
            </button>
            <a
              href="https://open.kakao.com/o/skdqupSh"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors mt-2 text-center min-h-[44px] flex items-center justify-center"
            >
              상담 문의
            </a>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;